/* global AfterPay */
/* eslint-disable react-hooks/exhaustive-deps, react/forbid-component-props */
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Field, Formik } from 'formik';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  PAYMENT_TOP_CTA_ID,
  PAYMENT_DOD_TRACKING_GATEWAY,
} from '../../../brands/default/constants';
import {
  BUTTON_PLACE_ORDER,
  DEFAULT_PAYMENT_ERROR,
  VIP_PAY_BUTTON_MESSAGE,
  VIP_PAY_BUTTON_MESSAGE_SELECTED,
  VIP_PAY_EXCLUSIVE_DEAL_MESSAGE,
  VIP_PAY_BUTTON_MESSAGE_EXCLUSIVE,
  ERROR_MESSAGE_CLEARPAY,
  CLEARPAY_DISABLED_MESSAGE,
} from '../../../brands/default/content/components/checkout';
import {
  trackEvent,
  trackPurchase,
  updateDod,
} from '../../../helpers/analytics';
import {
  createCheckout,
  getCheckoutData,
  preparePaymentData,
  sendClearpayPayment,
} from '../../../helpers/clearpay';
import { prepareTrackingData } from '../../../helpers/general';
import Message, { MESSAGE_TYPES } from '../../_generic/Message';
import CTAButton from '../../_generic/button/CTAButton';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import ClearpayModal from '../../clearpay/ClearpayModal';
import BasketSocialCues from '../BasketSocialCues';
import ButtonGoToPayment from './ButtonGoToPayment';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentSummary from './PaymentSummary';
import VipMessageWrapper from './VipMessageWrapper';
import VipToggleButtons from './VipToggleButtons';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentClearpay = ({
  afterError,
  atolProtected,
  basketProducts,
  currencySymbol,
  deviceFingerprintId,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  payFinalOrderAmount,
  promoCode,
  setVipOptIn,
  totalQuantity,
  travelDisableButton,
  vipOptIn = null,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  welcomeVipEnabled,
  basketDiscountForExistingVipUser,
}) => {
  const formikRef = React.useRef(null);
  const toast = useToast();
  const [showClearpayModal, setShowClearpayModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [clearpayToken, SetClearpayToken] = useState(null);
  const userProfile = useSelector((state) => state.user.userprofile);
  const [accountIncomplete, setAccountIncomplete] = useState(false);
  const isAccountCompleted = Boolean(
    userProfile.postcode &&
      userProfile.addressLine1 &&
      userProfile.town &&
      userProfile.firstName &&
      userProfile.lastName,
  );
  const ref = useRef(null);

  const handleVipToggleClick = (vipOptInValue) => {
    setVipOptIn(vipOptInValue);
  };

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const onClickHandler = () => {
    setShowClearpayModal(true);
  };

  const createSession = async () => {
    if (payFinalOrderAmount < 1 || payFinalOrderAmount > 1_000) return;
    const checkoutData = getCheckoutData({
      basketProducts,
      finalOrderAmount: payFinalOrderAmount,
      orderSummary,
      userProfile,
    });
    try {
      const response = await createCheckout(checkoutData);
      if (response?.token) {
        SetClearpayToken(response.token);
      } else {
        notifyError(ERROR_MESSAGE_CLEARPAY);
        afterError();
      }
    } catch {
      notifyError(DEFAULT_PAYMENT_ERROR);
      afterError();
    }

    toggleLoaderOff();
  };

  const sendPayment = async (token) => {
    const paymentData = await preparePaymentData({
      basketDiscountForExistingVipUser,
      basketProducts,
      deviceFingerprintId,
      finalOrderAmount: payFinalOrderAmount,
      isWalletApplied,
      orderSummary,
      promoCode,
      sessionId: false,
      token,
      vipOptIn,
    });
    const order = await sendClearpayPayment(paymentData);

    if (order) {
      const purchaseOrder = prepareTrackingData({
        basketProducts,
        order,
        orderSummary,
        paymentData,
        type: 'Clearpay',
      });
      trackEvent('clearpay_successful');
      trackPurchase(basketProducts, purchaseOrder, false);
      orderComplete(order);
    } else {
      trackEvent('clearpay_failed');
      notifyError(ERROR_MESSAGE_CLEARPAY);
      createSession();
    }
    setPaymentInProcess(false);
  };

  const authorizePayment = async () => {
    setPaymentInProcess(true);
    trackEvent('clearpay_click');
    if (welcomeVipEnabled && vipOptIn) trackEvent('vip_clearpaybw_click');
    if (welcomeVipEnabled && !vipOptIn) trackEvent('vip_clearpaybwo_click');
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.clearpay });
    AfterPay.initialize({
      countryCode: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
    });
    AfterPay.open();
    AfterPay.onComplete = (event) => {
      if (event.data.status === 'SUCCESS') {
        sendPayment(event.data.orderToken);
      }
      if (event.data.status === 'CANCELLED') {
        trackEvent('clearpay_aborted');
        setPaymentInProcess(false);
      } else if (
        event.data.status !== 'SUCCESS' &&
        event.data.status !== 'CANCELLED'
      ) {
        notifyError(ERROR_MESSAGE_CLEARPAY);
        trackEvent('clearpay_failed');
        setPaymentInProcess(false);
        createSession();
      }
    };
    AfterPay.transfer({ token: clearpayToken });
  };

  useEffect(() => {
    setAccountIncomplete(!isAccountCompleted);
    if (isAccountCompleted) {
      toggleLoaderOn();
      createSession();
    }
  }, [isAccountCompleted, userProfile]);

  useEffect(() => {
    toggleLoaderOn();
    createSession();
    formikRef.current.validateForm();
  }, [payFinalOrderAmount, vipOptIn]);

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      {accountIncomplete && (
        <Message
          extraClasses="login-to-purchase"
          message={CLEARPAY_DISABLED_MESSAGE}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={{
          cardCvv: '',
          confirmPayment: true,
          isVipOptedIn: vipOptIn,
        }}
        innerRef={formikRef}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }
          if (!isVipUser && welcomeVipEnabled && vipOptIn === null) {
            errors.isVipOptedIn = true;
          }

          return errors;
        }}
      >
        {({ handleSubmit, isValid, isSubmitting }) => (
          <form className="" name="payment" onSubmit={handleSubmit}>
            <div className="row payment-body">
              <div className="col-12">
                <div className="addin-text">
                  <p>
                    When you click &quot;Place your order&quot; you will be
                    redirected to Clearpay to complete your order.{' '}
                    <a>
                      <u
                        className="clearpay-link"
                        onClick={onClickHandler}
                        onKeyDown={onClickHandler}
                        role="link"
                        tabIndex="0"
                      >
                        More Info
                      </u>
                    </a>
                  </p>
                </div>
                <PaymentAgreeTerms disabled={false} />
                <BasketSocialCues
                  atolProtected={atolProtected}
                  className="with-margin"
                  isLighter
                />
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  totalQuantity={totalQuantity}
                />
              </div>
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
              {!isVipUser && welcomeVipEnabled && (
                <>
                  <VipToggleButtons
                    currencySymbol={currencySymbol}
                    finalOrderAmount={finalOrderAmount}
                    isExclusiveProductInBasket={isExclusiveProductInBasket}
                    setPayButton={handleVipToggleClick}
                    vipOptInFinalOrderAmount={vipOptInFinalOrderAmount}
                  />
                  <Field
                    id="isVipOptedIn"
                    name="isVipOptedIn"
                    type="hidden"
                    value={vipOptIn || ''}
                  />
                  <div className="vip-messages">
                    {isExclusiveProductInBasket && (
                      <div className="vip-pay-button-message">
                        <Message message={VIP_PAY_EXCLUSIVE_DEAL_MESSAGE} />
                      </div>
                    )}
                    {isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={VIP_PAY_BUTTON_MESSAGE_EXCLUSIVE}
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {!isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={VIP_PAY_BUTTON_MESSAGE}
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {vipOptIn !== null && (
                      <div className="vip-pay-button-message">
                        {VIP_PAY_BUTTON_MESSAGE_SELECTED}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12">
                <div
                  className={classNames({
                    'vip-pay-button-container': !isVipUser && welcomeVipEnabled,
                  })}
                >
                  <CTAButton
                    className="full-width buy-with-vip-button"
                    data-qa="payWithClearpay"
                    disabled={
                      travelDisableButton ||
                      paymentInProcess ||
                      isSubmitting ||
                      !isValid ||
                      accountIncomplete
                    }
                    id="clearpay-button"
                    onClick={() => authorizePayment()}
                    type="submit"
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      style={{ height: 13, marginRight: '4px', width: 13 }}
                    />
                    {BUTTON_PLACE_ORDER}
                  </CTAButton>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
        <ButtonGoToPayment />
      </ClientOnlyPortal>
      {showClearpayModal && (
        <ClearpayModal
          currentRef={ref.current}
          price={finalOrderAmount}
          setShowClearpayModal={setShowClearpayModal}
          showClearpayModal={showClearpayModal}
        />
      )}
      <style jsx>{`
        .disabled-overlay {
          position: relative;
          margin-bottom: -8px;
        }
        .disabled-overlay::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: grey;
          opacity: 0.3;
          z-index: 1000;
        }
        .clearpay-link {
          color: black;
          text-decoration: underline;
          white-space: nowrap;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }
        .vip-pay-button-container {
          margin: 0 auto 10px auto;
          width: 100%;
        }
        .vip-messages {
          margin: 0 auto;
          padding: 0 10px;
        }
        .vip-pay-button-message {
          margin: 0 auto 10px auto;
          text-align: center;
        }
      `}</style>
    </>
  );
};

PaymentClearpay.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketDiscountForExistingVipUser: PropTypes.number,
  basketProducts: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string,
  deviceFingerprintId: PropTypes.string,
  finalOrderAmount: PropTypes.number,
  isExclusiveProductInBasket: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  payFinalOrderAmount: PropTypes.number,
  promoCode: PropTypes.object,
  setVipOptIn: PropTypes.func,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPreButtonMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentClearpay;
